import React, { FC, useEffect } from 'react';
import { Row, Col, Form, Select as BaseSelect, Alert } from 'antd';
import { DatePicker, Input, Select } from '@cloudcar-app/cloudcar-components';
import { formatRut, validateRut } from '@abstract-cl/rut-ts';
import { FormInstance } from 'antd/es/form/Form';
import rutFormValidator from 'utils/rutFormValidator';
import { Customer, Insurance } from 'apollo/types';
import communes from 'utils/comunes';
import dealershipOptions from 'utils/kaufmann/dealershipMapper';
import { InfoCircleOutlined } from '@ant-design/icons';
import styles from './InsuranceProposalFormModal.module.scss';

const mapDealershipOptions = dealershipOptions.map((option) => ({
  label: option.label,
  value: option.value,
}));

interface BciModalProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: FormInstance<any>;
  insuranceData?: Insurance;
  customerData?: Customer;
}

const bciColor = [
  'AMARILLO',
  'AZUL',
  'AZUL METALICO',
  'BEIGE',
  'BLANCO',
  'BURDEO',
  'CAFE',
  'CAFE CLARO',
  'CALIPSO',
  'CELESTE',
  'COMBINACION',
  'DORADO',
  'GRIS',
  'MARFIL',
  'MARRON',
  'NARANJO',
  'NEGRO',
  'NO DEFINIDO',
  'PLATEADO',
  'ROJO',
  'ROJO COBRE',
  'SIN INFORMACIÓN',
  'VERDE',
  'VERDE CLARO',
  'VERDE METALICO',
  'VERDE PETROLEO',
  'VIOLETA',
];

const BciModal: FC<BciModalProps> = (props: BciModalProps) => {
  const { form, insuranceData, customerData } = props;
  const { Option } = BaseSelect;

  const handleCustomerChangeRut = (rut: string): string => {
    if (validateRut(rut)) {
      const formatted: string = formatRut(rut) || '';
      form.setFieldsValue({ rut: formatted });
      return rut;
    }
    form.setFieldsValue({
      rut: rut.split('.').join('').split('-').join(''),
    });
    return rut;
  };

  useEffect(() => {
    if (insuranceData?.insuranceInput) {
      form.setFieldsValue({
        carPlate: insuranceData.insuranceInput.carPlate || undefined,
        insuredName:
          insuranceData.insuranceInput.businessName ||
          insuranceData.insuranceInput.name,
        insuredFirstSurname: insuranceData.insuranceInput.businessName
          ? '.'
          : insuranceData.insuranceInput.firstSurname,
        insuredEmail: insuranceData.insuranceInput.email,
        insuredPhone: insuranceData.insuranceInput.phone,
        insuredRut: insuranceData.insuranceInput.rut,
      });
    }
  }, [insuranceData]);

  useEffect(() => {
    if (customerData && !insuranceData?.insuranceInput?.businessName) {
      form.setFieldsValue({
        rut: customerData.rut,
      });
    }
  }, [insuranceData, customerData]);

  return (
    <>
      <Row gutter={[0, 12]}>
        <Col xs={24}>
          <h3 className={styles.SubTitle}>Datos del cliente</h3>
        </Col>
        <Col xs={24}>
          {(insuranceData?.insuranceInput?.rut !== customerData?.rut ||
            insuranceData?.insuranceInput.businessName) && (
            <>
              <p style={{ marginBottom: '12px' }}>
                {' '}
                {insuranceData?.insuranceInput.businessName
                  ? 'Contratante (Representante legal)'
                  : 'Contratante'}{' '}
              </p>
              <Row gutter={[12, 12]} style={{ alignItems: 'center' }}>
                <Col xs={12}>
                  <Form.Item
                    name="name"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el nombre del contratante',
                      },
                    ]}
                  >
                    <Input placeholder="Nombre" compact allowClear />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="firstSurname"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el apellido del contratante',
                      },
                    ]}
                  >
                    <Input placeholder="Apellido" compact allowClear />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="email"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el correo del contratante',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Correo electrónico"
                      compact
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="rut"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el rut',
                      },
                      {
                        validator: rutFormValidator,
                      },
                    ]}
                  >
                    <Input
                      compact
                      placeholder="Rut"
                      allowClear
                      onChange={(e) => handleCustomerChangeRut(e.target.value)}
                      disabled={!insuranceData?.insuranceInput.businessName}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="phone"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el telefono del contratante',
                      },
                    ]}
                  >
                    <Input placeholder="Telefono" required compact allowClear />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="address"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese la dirección del contratante',
                      },
                    ]}
                  >
                    <Input placeholder="Dirección" compact allowClear />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="addressNumber"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message:
                          'Ingrese el número de la dirección del contratante',
                      },
                    ]}
                  >
                    <Input
                      placeholder="N° de la dirección"
                      compact
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="commune"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Selecciona la comuna',
                      },
                    ]}
                  >
                    <Select
                      label="Comuna"
                      placeholder="Comuna"
                      required
                      compact
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        option
                          ? ((option.children as unknown as string) || '')
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          : false
                      }
                    >
                      {communes.bciCommunes.map((item) => (
                        <Option key={item.code} value={item.code.toString()}>
                          {item.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <p style={{ marginBottom: '12px', marginTop: '12px' }}> Asegurado </p>
          <Row gutter={[12, 12]} style={{ alignItems: 'center' }}>
            <Col xs={12}>
              <Form.Item name="insuredName" className={styles.FormItem}>
                <Input compact disabled />
              </Form.Item>
            </Col>
            {!insuranceData?.insuranceInput?.businessName && (
              <Col xs={12}>
                <Form.Item
                  name="insuredFirstSurname"
                  className={styles.FormItem}
                >
                  <Input compact disabled />
                </Form.Item>
              </Col>
            )}

            <Col xs={12}>
              <Form.Item name="insuredEmail" className={styles.FormItem}>
                <Input compact disabled />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="insuredRut" className={styles.FormItem}>
                <Input compact disabled />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="insuredPhone" className={styles.FormItem}>
                <Input compact disabled />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="insuredAddress"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese la dirección del asegurado',
                  },
                ]}
              >
                <Input placeholder="Dirección" compact allowClear />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="insuredAddressNumber"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el número de la dirección del asegurado',
                  },
                ]}
              >
                <Input placeholder="N° de la dirección" compact allowClear />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="insuredCommune"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Selecciona la comuna',
                  },
                ]}
              >
                <Select
                  label="Comuna"
                  placeholder="Comuna"
                  required
                  compact
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option
                      ? ((option.children as unknown as string) || '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      : false
                  }
                >
                  {communes.bciCommunes.map((item) => (
                    <Option key={item.code} value={item.code.toString()}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row gutter={[0, 12]}>
        <Col xs={24}>
          <h3 className={styles.SubTitle}>Datos de la venta</h3>
        </Col>
        <Col xs={24}>
          <Row gutter={[12, 12]} style={{ alignItems: 'center' }}>
            <Col xs={12}>
              <Form.Item
                name="carColor"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el color del vehículo',
                  },
                ]}
              >
                <Select
                  label="Color"
                  placeholder="Color"
                  compact
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option
                      ? ((option.children as unknown as string) || '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      : false
                  }
                >
                  {bciColor.map((item) => (
                    <Option key={item} value={item.toString()}>
                      {item}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="carEngineId"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el n° del motor del vehículo',
                  },
                  {
                    max: 22,
                    message:
                      'El número de motor no puede tener más de 22 caracteres',
                  },
                ]}
              >
                <Input placeholder="N° motor vehículo" compact allowClear />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="carChassis"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese el chasis del vehículo',
                  },
                ]}
              >
                <Input placeholder="Chasis del vehículo" compact allowClear />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="carPlate"
                className={styles.FormItem}
                rules={[
                  {
                    required: true,
                    message: 'Ingrese la patente del vehículo',
                  },
                  {
                    pattern: /^[A-Za-z0-9]{6}$/,
                    message:
                      'La patente debe contener solo letras y números. Debe tener 6 caracteres.',
                  },
                ]}
              >
                <Input
                  placeholder="Patente del vehículo"
                  compact
                  allowClear
                  disabled={!!insuranceData?.insuranceInput.carPlate}
                  onChange={(e) => {
                    const filteredValue = e.target.value
                      .replace(/[^A-Za-z0-9]/g, '')
                      .slice(0, 6);
                    form.setFieldsValue({ carPlate: filteredValue });
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="carDealer" className={styles.FormItem}>
                <Select
                  label="Dealer"
                  placeholder="Dealer (opcional)"
                  compact
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option
                      ? ((option.children as unknown as string) || '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      : false
                  }
                >
                  {mapDealershipOptions.map((item) => (
                    <Option key={item.label} value={item.label.toString()}>
                      {item.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {!insuranceData?.insuranceInput.carPlate && (
              <>
                <Col xs={12}>
                  <Form.Item
                    name="invoiceId"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese el n° de factura',
                      },
                    ]}
                  >
                    <Input placeholder="N° factura" compact allowClear />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="invoiceDate"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: 'Ingrese la fecha de factura',
                      },
                      {
                        validator(rule, value: string, callback) {
                          if (value) {
                            const date = new Date(value);
                            const today = new Date();
                            const diffTime = Math.abs(
                              today.getTime() - date.getTime(),
                            );
                            const diffDays = Math.ceil(
                              diffTime / (1000 * 60 * 60 * 24),
                            );
                            if (diffDays > 3) {
                              callback(
                                'La factura no puede tener más de 2 días de antigüedad',
                              );
                            }
                          }
                          callback();
                        },
                      },
                    ]}
                  >
                    <DatePicker
                      className={styles.DatePicker}
                      placeholder="Fecha de factura"
                      compact
                      required
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            <Col xs={12}>
              <Alert
                showIcon
                style={{ borderRadius: '4px' }}
                icon={<InfoCircleOutlined />}
                message={
                  <p style={{ fontSize: '12px' }}>
                    {insuranceData?.insuranceInput.carPlate
                      ? 'Vehículo semi nuevo. No se necesita información de factura.'
                      : 'La factura no puede tener más de 2 días de antigüedad.'}
                  </p>
                }
                type="info"
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

BciModal.defaultProps = {
  insuranceData: undefined,
  customerData: undefined,
};

export default BciModal;
